import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  Card,
  Alert,
  Container,
  Table,
  Spinner,
  Modal,
  Dropdown,
  ButtonGroup,
  Row,
  Col,
  Form,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { useAuth } from "../../firebaseContext";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse,
  faUserPlus,
  faArrowsRotate,
  faMagnifyingGlass,
  faEye,
  faEnvelopeOpenText,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

export default function ManageUsers() {
  const navigate = useNavigate();
  const { getUsersCollection, resetPassword } = useAuth();

  const [showConfirm, setShowConfirm] = useState(false);
  const [alert, setAlert] = useState(null);
  const [holdAction, setHoldAction] = useState(null);
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUsersCollection();
      setUserData(data);
    } catch (err) {
      setAlert("Error loading users.");
    } finally {
      setLoading(false);
    }
  }, [getUsersCollection]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  async function handleConfirmAction(e) {
    e.preventDefault();
    if (holdAction?.action === "resetPassword" && holdAction?.email) {
      try {
        await resetPassword(holdAction.email);
        setAlert(`A password reset link has been sent to ${holdAction.email}.`);
      } catch (err) {
        setAlert("Error: " + err);
      }
    }
    setShowConfirm(false);
    setHoldAction(null);
  }

  function handleActions(e, action, email) {
    e.preventDefault();
    setHoldAction({ action, email });
    setShowConfirm(true);
  }

  function handleUserProfile(e, email) {
    e.preventDefault();
    navigate("/user", {
      state: { email },
      replace: false,
    });
  }

  const filterActive = filterText.trim().length > 0;
  const filteredUsers = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) {
      return userData;
    }
    return userData.filter((user) => user[0]?.toLowerCase().includes(query));
  }, [filterText, userData]);

  const totalUsers = userData.length;
  const visibleUsers = filteredUsers.length;

  return (
    <Container fluid className="py-4 manage-users-page">
      <Row className="justify-content-center">
        <Col xl={10} lg={11}>
          <Card className="border-0 shadow-sm bg-primary text-white mb-4">
            <Card.Body>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                <div>
                  <Badge bg="light" text="dark" className="text-uppercase mb-2">
                    Admin tools
                  </Badge>
                  <h1 className="display-6 fw-semibold mb-1">Manage users</h1>
                  <p className="mb-0">
                    Review portal accounts, open a profile, or share a reset
                    link in seconds.
                  </p>
                </div>
                <div className="text-md-end">
                  <p className="text-uppercase small text-white-50 mb-1">
                    Total users
                  </p>
                  <h2 className="fw-bold mb-2">
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    {totalUsers}
                  </h2>
                  {filterActive && (
                    <Badge bg="light" text="dark">
                      Showing {visibleUsers}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2 mt-4">
                <Button
                  variant="light"
                  className="text-primary"
                  onClick={() => navigate("/")}
                >
                  <FontAwesomeIcon icon={faHouse} className="me-2" />
                  Back to home
                </Button>
                <Button
                  variant="success"
                  onClick={() => navigate("/createUser")}
                >
                  <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                  Create user
                </Button>
                <Button
                  variant="outline-light"
                  onClick={loadUsers}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                  Refresh list
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <Alert variant="info" className="mb-4">
                User data will appear or update after they log in.
              </Alert>

              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div>
                  <h2 className="h4 mb-1">Directory</h2>
                  <p className="text-muted mb-0">
                    Search for an email address to view the profile or send a
                    reset link.
                  </p>
                </div>
                <InputGroup className="manage-users-search">
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search by email"
                    value={filterText}
                    onChange={(event) => setFilterText(event.target.value)}
                    disabled={loading || !totalUsers}
                  />
                  {filterActive && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setFilterText("")}
                      disabled={loading}
                    >
                      Clear
                    </Button>
                  )}
                </InputGroup>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : visibleUsers > 0 ? (
                <div className="table-responsive">
                  <Table
                    striped
                    hover
                    responsive
                    size="sm"
                    className="align-middle"
                  >
                    <thead className="table-light">
                      <tr>
                        <th scope="col">Email address</th>
                        <th scope="col" className="text-center">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user[0]}>
                          <td className="fw-semibold">{user[0]}</td>
                          <td className="text-center">
                            <Dropdown as={ButtonGroup} align="end">
                              <Button variant="outline-primary" size="sm">
                                Actions
                              </Button>
                              <Dropdown.Toggle
                                split
                                variant="outline-primary"
                                id={`dropdown-split-${user[0]}`}
                                size="sm"
                              />
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  onClick={(e) => handleUserProfile(e, user[0])}
                                >
                                  <FontAwesomeIcon
                                    icon={faEye}
                                    className="me-2"
                                  />
                                  View profile
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={(e) =>
                                    handleActions(e, "resetPassword", user[0])
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={faEnvelopeOpenText}
                                    className="me-2"
                                  />
                                  Reset password
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="py-5 text-center text-muted border rounded">
                  <p className="mb-1 fw-semibold">No users to display.</p>
                  <p className="mb-0">
                    Try adjusting your search or refresh the list to fetch the
                    latest accounts.
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirm Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to{" "}
          {holdAction?.action === "resetPassword" ? "reset password for" : ""}{" "}
          <b>{holdAction?.email}</b>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleConfirmAction}>
            Confirm
          </Button>
          <Button variant="danger" onClick={() => setShowConfirm(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Alert Modal */}
      <Modal show={!!alert} onHide={() => setAlert(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>{alert}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setAlert(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
